import React, { useEffect } from 'react';
import { useImmer } from 'use-immer';
import Select from 'react-select';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/common/Button';
import {
  updateCollectionSettings,
  resetUpdateCollectionSettingsState,
  viewCollectionOverview,
} from 'store/collections/general/actions.js';
import Spinner from 'components/common/Spinner';
import { fetchSystemMetaData } from 'store/misc/actions.js';
import { fetchUserTeams } from 'store/user/userTeams/userTeamsActions.js';
import { capitalizeFirstLetter } from 'utils/misc.js';

const staticCollectionWorkflowOptions = {
  intrigueio_pre_collection: {
    value: 'intrigueio_pre_collection',
    label: 'Intrigue.io Pre Collection',
  },
  intrigueio_standard_collection: {
    value: 'intrigueio_standard_collection',
    label: 'Intrigue.io Standard Collection',
  },
  intrigueio_premium_collection: {
    value: 'intrigueio_premium_collection',
    label: 'Intrigue.io Premium Collection',
  },
};

const staticAutoRefreshOptions = {
  never: { value: 'never', label: 'Never' },
  'ad-hoc': { value: 'ad-hoc', label: 'Ad-hoc' },
  daily: { value: 'daily', label: 'Daily' },
  weekly: { value: 'weekly', label: 'Weekly' },
  monthly: { value: 'monthly', label: 'Monthly' },
};

const customStyles = {
  menu: (provided, state) => ({ ...provided, marginTop: '1px' }),
};

const SettingsMain = (props) => {
  const dispatch = useDispatch();
  const collectionName = props.match.params.id;

  const viewCollectionState = useSelector(
    (state) => state.collection.generalSettings.overview
  );
  const updateCollectionState = useSelector(
    (state) => state.collection.generalSettings.update
  );
  const userTeamsState = useSelector((state) => state.user.teams);
  const systemMetadata = useSelector((state) => state.misc.fetchSystemMetadata);

  const [state, setState] = useImmer({
    collectionOwner: null,
    collectionWorkflow: '',
    collectionSlug: '/p/uneditable_slug',
    collectionPrintableName: 'Collection Name',
    autoRefresh: '',
    collectionOwnerOptions: [{ value: 'none', label: 'None' }],
  });

  useEffect(() => {
    dispatch(fetchSystemMetaData());
    dispatch(fetchUserTeams());
  }, []);

  useEffect(() => {
    console.log('view collection state trigger');
    if (viewCollectionState?.data !== null) {
      setState((draft) => {
        draft.collectionOwner = {
          value: viewCollectionState?.data?.owner_uuid,
          label: viewCollectionState?.data?.owner_name,
        };
        draft.autoRefresh =
          staticAutoRefreshOptions[
            viewCollectionState?.data?.auto_refresh_schedule
          ];
        draft.collectionWorkflow = {
          value: viewCollectionState?.data?.workflow_name,
          label: capitalizeFirstLetter(
            viewCollectionState?.data?.workflow_name
              ? viewCollectionState?.data?.workflow_name.split('_').join(' ')
              : ''
          ),
        };
        draft.collectionPrintableName =
          viewCollectionState?.data?.printable_name;
      });
    }
  }, [viewCollectionState]);

  // custom methods definition
  const onChangeSelect = (value, target) => {
    console.log('on change select', target.name, target);
    setState((draft) => {
      draft[target.name] = value;
    });
  };

  const onChangeCollectionPrintableName = (e) => {
    const value = e.target.value;
    setState((draft) => {
      draft.collectionPrintableName = value;
    });
  };

  const onUpdateCollectionSettings = () => {
    console.log('state on update', state);
    const postPayload = {
      name: collectionName,
      pretty_name: state.collectionPrintableName,
      workflow: state.collectionWorkflow.value,
      auto_refresh: state.autoRefresh.value,
      owner_uuid: state.collectionOwner.value,
    };
    console.log('postPayload', postPayload);
    dispatch(updateCollectionSettings(collectionName, postPayload));
  };

  const renderTeams = () => {
    const teams = userTeamsState?.data?.length
      ? userTeamsState?.data.map((item) => {
          return { value: item.uuid, label: item.name };
        })
      : [];
    console.log('teams mapping', teams);

    return (
      <React.Fragment>
        <Select
          //   inputValue={state.collectionOwner}
          styles={customStyles}
          options={teams}
          value={state.collectionOwner}
          defaultValue={state.collectionOwner}
          isSearchable={false}
          name="collectionOwner"
          onChange={onChangeSelect}
          placeholder="Select Owner"
          //   defaultInputValue={state.collectionOwner}
        />
      </React.Fragment>
    );
  };

  const renderWorkflowOptions = () => {
    const workflowOptions = systemMetadata?.data?.valid_workflows?.length
      ? systemMetadata?.data?.valid_workflows.map((item) => {
          return { value: item.name, label: item.pretty_name };
        })
      : [];
    console.log('workflowOptions mapping', workflowOptions);

    return (
      <React.Fragment>
        <Select
          //   inputValue={state.collectionOwner}
          styles={customStyles}
          options={workflowOptions}
          value={state.collectionWorkflow}
          defaultValue={state.collectionWorkflow}
          isSearchable={false}
          name="collectionWorkflow"
          onChange={onChangeSelect}
          placeholder="Select Workflow Option"
          //   defaultInputValue={state.collectionOwner}
        />
      </React.Fragment>
    );
  };

  const autoRefreshOptions = Object.values(staticAutoRefreshOptions);
  const workflowOptions = Object.values(staticCollectionWorkflowOptions);

  return userTeamsState.loading || systemMetadata.loading ? (
    <div className="mx-4 my-4 px-6 py-10 flex items-center justify-center">
      <Spinner loaderSize="lg" />
    </div>
  ) : (
    <div className="">
      <div className="flex  flex-wrap w-full mt-8">
        <div className="w-3/12">Collection Owner</div>
        <div className="pl-3 w-9/12 ">{renderTeams()}</div>
      </div>

      <hr className="bg-gray-700 my-8" />

      <div className="flex  flex-wrap w-full mt-8">
        <div className="w-3/12">Collection Slug</div>
        <div className="pl-3 w-9/12 ">
          <input
            className="px-2 py-2 rounded-md border border-gray-500 bg-gray-300 text-gray-500 w-full"
            disabled={true}
            value={state.collectionSlug}
            type="text"
          />
        </div>
      </div>

      <div className="flex  flex-wrap w-full mt-8">
        <div className="w-3/12">Collection Name</div>
        <div className="pl-3 w-9/12 ">
          <input
            name="collectionPrintableName"
            className="px-2 py-2 rounded-md border border-gray-500 w-full"
            // disabled={true}
            placeholder="Collection Name"
            value={state.collectionPrintableName}
            onChange={onChangeCollectionPrintableName}
            type="text"
          />
        </div>
      </div>

      <hr className="bg-gray-700 my-8" />

      <div className="flex  flex-wrap w-full mt-8">
        <div className="w-3/12">Collection Workflow</div>
        <div className="pl-3 w-9/12 ">{renderWorkflowOptions()}</div>
      </div>

      <div className="flex  flex-wrap w-full mt-8">
        <div className="w-3/12">Auto Refresh</div>
        <div className="pl-3 w-9/12 ">
          <Select
            // inputValue={defaultValue}
            styles={customStyles}
            options={autoRefreshOptions}
            value={state.autoRefresh}
            isSearchable={false}
            name="autoRefresh"
            onChange={onChangeSelect}
            placeholder="Select Auto refresh option"
            // defaultValue={defaultValue}
            // defaultInputValue={defaultValue}
          />
        </div>
      </div>

      <div className="text-right mt-4 mb-8">
        <Button
          intent="primary"
          showSpinner={true}
          type="submit"
          loading={
            viewCollectionState.loading || updateCollectionState.loading
              ? true
              : false
          }
          //   disable={userLoginState.loading ? false : isSubmitting}
          onClick={onUpdateCollectionSettings}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default SettingsMain;
